import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  VoiceRecordingTranscriptSettingEditForm,
  VoiceRecordingTranscriptSettingEditInitialValues,
  VoiceRecordingTranscriptSettingComponents,
  VoiceRecordingTranscriptSettingAdditionalParams,
  VoiceRecordingTranscriptSettingFormOptions,
  onVoiceRecordingTranscriptSettingFormSaved,
  IVoiceRecordingTranscriptSettingFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedVoiceRecordingTranscriptSetting, ISharedPatchVoiceRecordingTranscriptSetting, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchVoiceRecordingTranscriptSetting, patchVoiceRecordingTranscriptSetting, getVoiceRecordingTranscriptSettingUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedVoiceRecordingTranscriptSetting> {}

const VoiceRecordingTranscriptSettingFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchVoiceRecordingTranscriptSetting(id).then(voice_recording_transcript_setting => setValue(voice_recording_transcript_setting));
  }, [id, setValue, ]);

  const param_props: IVoiceRecordingTranscriptSettingFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && VoiceRecordingTranscriptSettingEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && VoiceRecordingTranscriptSettingFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchVoiceRecordingTranscriptSetting, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchVoiceRecordingTranscriptSetting) => value && save(() => patchVoiceRecordingTranscriptSetting(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onVoiceRecordingTranscriptSettingFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.VoiceRecordingTranscriptSetting, value)}
    >
      <AtlasForm<ISharedPatchVoiceRecordingTranscriptSetting>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={VoiceRecordingTranscriptSettingComponents}
        additionalParams={VoiceRecordingTranscriptSettingAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={VoiceRecordingTranscriptSettingEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('VOICE_RECORDING_TRANSCRIPT_SETTING')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const VoiceRecordingTranscriptSettingFormEditView = React.memo(withEdit(VoiceRecordingTranscriptSettingFormEdit, getVoiceRecordingTranscriptSettingUpdateSchema));
